import { useMemo, useEffect, useCallback } from 'react';
import type {
  EntityRootData,
  EntityDetailSchema,
  DomainEntity,
  EntityStatus,
  TreeNode,
  ConfigEntityMeta,
} from '@web-config-app/core';
import { isUsingConfigAppBackend } from '@web-config-app/common';
import { get } from 'lodash-es';
import { useEntityTree } from '../use-entity-tree/use-entity-tree';
import { useEntityTreeState } from '../use-entity-tree-state/use-entity-tree-state';

export interface UseEntityDetailsProps {
  rootSchema: EntityDetailSchema;
  domainEntity: DomainEntity;
  entityRootData: EntityRootData | undefined;
  entityMeta: ConfigEntityMeta | undefined;
  setEntityData: (data: any, path: string) => void;
  path?: string;
  instanceId?: string;
  onTreeNodeSelect?: (args: any) => void;
  name: string | undefined;
  defaultEntityName?: string;
  status: EntityStatus;
}

export type UseEntityDetailsResult = ReturnType<typeof useEntityDetails>;

const rootPaths = ['root', ''];
const getFullEntityDataPath = (
  path: string,
  isEntityUsingConfigAppBackend?: boolean,
) => {
  const baseDataPath = isUsingConfigAppBackend({
    entityLevelOverride: isEntityUsingConfigAppBackend,
  })
    ? 'attributes.domainEntityAttributes'
    : 'attributes';
  return rootPaths.includes(path) ? baseDataPath : `${baseDataPath}.${path}`;
};

export const useEntityDetails = ({
  rootSchema,
  domainEntity,
  entityRootData,
  entityMeta,
  path,
  instanceId,
  onTreeNodeSelect,
  setEntityData,
  defaultEntityName,
  name,
  status,
}: UseEntityDetailsProps) => {
  const {
    expandedNodes,
    toggleNode,
    expandParentNodesForPath,
    resetNodes: resetEntityTreeNodes,
  } = useEntityTreeState((state) => state);

  const { entityTree, nodeMap, handleNodeToggle } = useEntityTree({
    schema: isUsingConfigAppBackend({
      entityLevelOverride: domainEntity.isUsingConfigAppBackend,
    })
      ? rootSchema.properties.attributes
      : rootSchema,
    data: entityRootData,
    onSelect: (node: TreeNode) => {
      const nodeId = typeof node === 'string' ? node : node?.id;
      onTreeNodeSelect?.(node);
      if (typeof nodeId === 'string') {
        toggleNode(nodeId);
      }
    },
    domainEntity,
  });

  const { formSchema, formData, formPath, formReady } = useMemo(() => {
    if (path) {
      const currentNode = nodeMap.get(path);
      if (currentNode && currentNode.data) {
        const {
          data: { schema: schemaForPath },
        } = currentNode;
        const dataPath = getFullEntityDataPath(
          path,
          domainEntity.isUsingConfigAppBackend,
        );
        const dataForPath = get(entityRootData, dataPath);
        const isLoaded = instanceId
          ? Boolean(instanceId && entityRootData)
          : true;
        return {
          formSchema: schemaForPath,
          formData: dataForPath,
          formPath: path,
          formReady: isLoaded,
        };
      }
    }

    return {
      formSchema: undefined,
      formData: undefined,
      formPath: undefined,
      formReady: false,
    };
  }, [
    entityRootData,
    nodeMap,
    path,
    domainEntity.isUsingConfigAppBackend,
    instanceId,
  ]);

  useEffect(() => {
    /**
     * Ensure that the node for the current path as well as any
     * parent nodes are expanded. This may be necessary in cases where
     * a form is opened via deep link or object card
     */
    if (formPath) {
      expandParentNodesForPath(formPath);
    }
  }, [formPath, expandParentNodesForPath]);
  /**
   * pass this function to the EntityForm so that as its data changes, it can update the `rootData` accordingly.
   */
  const onFormDataChange = (updatedFormData: any) => {
    const dataPath = getFullEntityDataPath(
      path ?? '',
      domainEntity.isUsingConfigAppBackend,
    );
    setEntityData(updatedFormData, dataPath);
  };

  const setEntityName = useCallback(
    (newName: string) => {
      if (
        isUsingConfigAppBackend({
          entityLevelOverride: domainEntity.isUsingConfigAppBackend,
        })
      ) {
        setEntityData(
          newName || defaultEntityName,
          'attributes.entityMetadata.name',
        );
      }
    },
    [defaultEntityName, domainEntity.isUsingConfigAppBackend, setEntityData],
  );

  const isReadOnly = entityRootData?.attributes?.entityMetadata?.readOnly;
  const entityStaticMessage = entityMeta?.staticMessage;

  return {
    name,
    status,
    instanceId,
    entityTree,
    nodeMap,
    expandedNodes,
    handleNodeToggle,
    setEntityData,
    setEntityName,
    resetEntityTreeNodes,
    rootSchema,
    formSchema,
    formData,
    formPath,
    formReady,
    onFormDataChange,
    isReadOnly,
    entityStaticMessage,
    defaultEntityName,
  };
};
